import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Building2, Shield, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import Sidebar from "../components/Sidebar";
import { useAuth } from "../lib/auth";
import FeedComposer from "../components/FeedComposer";
import FeedCard from "../components/FeedCard";

const TABS = [
  { key: "latest", label: "Latest" },
  { key: "top",    label: "Top" },
  { key: "unread", label: "Unread" },
];

export default function Internal() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const myAlias = user?.public_alias;

  const [searchParams, setSearchParams] = useSearchParams();
  const expandedPostId = searchParams.get("post");
  const tab = searchParams.get("sort") || "latest";
  const period = searchParams.get("period") || (tab === "top" ? "30d" : "all");

  const [posts, setPosts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [dealership, setDealership] = useState("");
  const [isAdminView, setIsAdminView] = useState(false);
  const [loading, setLoading] = useState(true);
  const [overrideDealer, setOverrideDealer] = useState("");

  const load = async (override) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (override) params.set("dealership", override);
      params.set("sort", tab);
      params.set("period", period);
      const r = await api.get(`/internal/posts?${params.toString()}`);
      setPosts(r.data.posts || []);
      setDealership(r.data.dealership || "");
      setIsAdminView(!!r.data.is_admin_view);
      setUnreadCount(r.data.unread_count || 0);
    } catch (e) {
      console.error("internal load", e);
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load(overrideDealer || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, period]);

  const setTab = (nextTab) => {
    const next = new URLSearchParams(searchParams);
    next.set("sort", nextTab);
    if (nextTab === "top") next.set("period", "30d"); else next.delete("period");
    next.delete("post");
    setSearchParams(next, { replace: true });
  };
  const setExpanded = (postId, on) => {
    const next = new URLSearchParams(searchParams);
    if (on) next.set("post", postId); else next.delete("post");
    setSearchParams(next, { replace: true });
  };

  const visible = useMemo(() => {
    let out = posts.filter(p => !p._deleted);
    // Phase 13.2 fix — same defensive id-dedupe as community feed.
    out = Array.from(new Map(out.map(p => [p.id, p])).values());
    return [...out].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
  }, [posts]);

  const onPostChange = (next) => {
    setPosts(ps => ps.map(p => p.id === next.id ? next : p).filter(p => !(p._deleted && p.id === next.id)));
    if (next._deleted) setPosts(ps => ps.filter(p => p.id !== next.id));
  };

  useEffect(() => {
    if (!expandedPostId || loading) return;
    const el = document.querySelector(`[data-testid="feed-card-${expandedPostId}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [expandedPostId, loading]);

  return (
    <div className="min-h-screen bg-[#F0F1F5] text-fc-navy flex" data-testid="internal-page">
      <Sidebar />
      <main className="flex-1 min-w-0 bg-[#F8F9FB] text-gray-900">
        {expandedPostId && (
          <div className="bg-white border-b border-[#E5E7EB] sticky top-0 z-40 lg:hidden">
            <button onClick={() => setExpanded(null, false)} className="px-4 py-2.5 inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase font-bold text-fc-red" data-testid="back-to-feed">
              <ArrowLeft size={13} /> Back to feed
            </button>
          </div>
        )}
        {/* Top strip */}
        <div className="bg-white border-b border-[#E5E7EB] sticky top-0 z-30">
          <div className="max-w-[760px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-[10px] tracking-[0.22em] uppercase font-black text-[#6B7280]">TEAM FEED</p>
              <h1 className="text-xl sm:text-2xl font-black tracking-tight text-fc-navy">Your dealership</h1>
            </div>
            <div className="flex items-center gap-2">
              <div className="inline-flex items-center gap-1.5 bg-gray-50 border border-[#E5E7EB] rounded-full px-3 py-1.5 text-xs font-bold text-fc-navy" data-testid="internal-scope-pill">
                <Building2 size={12} /> {dealership || "—"}
              </div>
              {isAdminView && (
                <span className="text-[10px] tracking-[0.18em] uppercase font-black bg-fc-red/10 text-fc-red px-2 py-1 rounded">
                  <Shield size={10} className="inline mr-1" /> ADMIN VIEW
                </span>
              )}
            </div>
          </div>

          {/* Admin: dealership switcher */}
          {isAdmin && (
            <div className="max-w-[760px] mx-auto px-4 sm:px-6 pb-3 flex items-center gap-2">
              <span className="text-[10px] tracking-[0.18em] uppercase font-bold text-[#6B7280]">VIEW AS:</span>
              <input
                value={overrideDealer}
                onChange={(e) => setOverrideDealer(e.target.value)}
                placeholder="dealership name"
                className="bg-gray-50 border border-[#E5E7EB] rounded-lg px-2 py-1 text-xs flex-1 max-w-[200px] focus:outline-none focus:border-fc-red text-fc-navy"
                data-testid="dealership-override"
              />
              <button onClick={() => load(overrideDealer)} className="text-[10px] tracking-[0.18em] uppercase font-black text-fc-red hover:underline">
                Switch
              </button>
              {isAdminView && (
                <button onClick={() => { setOverrideDealer(""); load(""); }} className="text-[10px] tracking-[0.18em] uppercase font-black text-[#6B7280] hover:text-fc-navy">
                  Reset
                </button>
              )}
            </div>
          )}

          {/* Tabs */}
          <div className="max-w-[760px] mx-auto px-4 sm:px-6">
            <div className="flex gap-1 border-b border-[#E5E7EB] -mx-1">
              {TABS.map(t => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`relative px-4 py-2.5 text-[13px] font-bold tracking-wide border-b-2 transition-colors ${
                    tab === t.key ? "border-fc-red text-fc-red" : "border-transparent text-[#6B7280] hover:text-fc-navy"
                  }`}
                  data-testid={`feed-tab-${t.key}`}
                >
                  {t.label}
                  {t.key === "unread" && unreadCount > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-fc-red text-white text-[10px] rounded-full font-black" data-testid="unread-badge">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-[760px] mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-8 sm:space-y-12 pb-32">
          {!isAdminView && <FeedComposer scope="internal" onPosted={() => load(overrideDealer || "")} dealershipLabel={dealership} />}

          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => <SkeletonCard key={i} />)}
            </div>
          ) : visible.length === 0 ? (
            <EmptyState dealership={dealership} />
          ) : (
            visible.map(p => (
              <FeedCard
                key={p.id}
                post={p}
                scope="internal"
                currentAlias={myAlias}
                isAdmin={isAdmin}
                onChange={onPostChange}
                expanded={p.id === expandedPostId}
                onToggleExpanded={(open) => setExpanded(p.id, open)}
              />
            ))
          )}
        </div>
      </main>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6 animate-pulse">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-gray-200" />
        <div className="flex-1 space-y-2">
          <div className="h-3 w-32 bg-gray-200 rounded" />
          <div className="h-2 w-24 bg-gray-100 rounded" />
        </div>
      </div>
      <div className="space-y-2">
        <div className="h-4 w-3/4 bg-gray-200 rounded" />
        <div className="h-4 w-full bg-gray-100 rounded" />
      </div>
    </div>
  );
}

function EmptyState({ dealership }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-12 text-center" data-testid="feed-empty">
      <div className="w-16 h-16 rounded-full bg-fc-red/10 mx-auto mb-4 flex items-center justify-center">
        <Building2 className="text-fc-red" size={24} />
      </div>
      <h3 className="text-lg font-black tracking-tight text-fc-navy mb-2">Quiet in here.</h3>
      <p className="text-[#6B7280] text-sm">
        Be the first at {dealership || "your dealership"} to share what you're working on.
      </p>
    </div>
  );
}
